import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { contractTypesApi } from "../../api/contractTypeApi";
import type { ContractType } from "../../types/contractType.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

interface ContractTypesState {
  items: ContractType[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: ContractTypesState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const contractTypesSlice = createSlice({
  name: "contractTypes",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setContractTypes: (state, action: PayloadAction<ContractType[]>) => {
      state.items = action.payload;
    },
    appendContractTypes: (state, action: PayloadAction<ContractType[]>) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleContractTypeLocally: (
      state,
      action: PayloadAction<ContractType>,
    ) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetContractTypeList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleContractType: (
      state,
      action: PayloadAction<Partial<ContractType> & { id: string }>,
    ) => {
      const index = state.items.findIndex((ct) => ct.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleContractType: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((ct) => ct.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setContractTypes,
  appendContractTypes,
  addSingleContractTypeLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetContractTypeList,
  updateSingleContractType,
  removeSingleContractType,
} = contractTypesSlice.actions;

interface FetchContractTypesPayload {
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  sortAscending?: boolean;
  groupIds?: string[];
  ids?: string[];
  searchText?: string;
  includeDeleted?: boolean;
  /** Dropdowns pass true so deactivated entries can't be picked;
   *  the listing filter passes null for "show both". */
  isActive?: boolean | null;
}

export const fetchContractTypes = (payload: FetchContractTypesPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().contractTypes;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        // This endpoint reads the flag as "ascending" despite its name — a
        // quirk every dropdown in the app relies on, so it is matched here
        // rather than changed there.
        sortDecending: payload.sortAscending ?? false,
        groupIds: payload.groupIds || [userData?.companyId],
        ids: payload.ids,
        searchText: payload.searchText || "",
        includeDeleted: payload.includeDeleted || false,
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
      };

      const response = await contractTypesApi.searchContractTypes(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setContractTypes(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendContractTypes(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Contract Types error:", error);
      toast.error(
        i18n.t("contractTypes:contractType.messages.fetchError", {
          defaultValue: "Failed to fetch contract types",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addContractType = (data: { contractType: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const payload = {
        contractName: data.contractType,
      };

      const res = await contractTypesApi.createContractType(payload);

      const createdId = res?.data?.id || res?.data || res?.id || res;

      const newContractType: ContractType = {
        contractName: data.contractType,
        id: typeof createdId === "string" ? createdId : createdId?.id,
        groupId: res?.data?.groupId || res?.groupId || "",
      };

      dispatch(addSingleContractTypeLocally(newContractType));

      toast.success(
        i18n.t("contractTypes:contractType.messages.addSuccess", {
          defaultValue: "Contract Type created successfully",
        }),
      );
      return newContractType;
    } catch (error) {
      console.error("Create Contract Type error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("contractTypes:contractType.messages.addError", {
          defaultValue: "Failed to create contract type",
        })));
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editContractType = (data: {
  id: string;
  contractType: string;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));

      const payload = {
        id: data.id,
        contractName: data.contractType,
      };

      const res = await contractTypesApi.updateContractType(payload);
      const updatedType = res?.data || payload;

      dispatch(updateSingleContractType(updatedType));

      toast.success(
        i18n.t("contractTypes:contractType.messages.editSuccess", {
          defaultValue: "Contract Type updated successfully",
        }),
      );
      return updatedType;
    } catch (error) {
      console.error("Edit Contract Type error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("contractTypes:contractType.messages.editError", {
          defaultValue: "Failed to update contract type",
        })));
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeContractType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleContractType(id));

      await contractTypesApi.removeContractType(id);

      toast.success(
        i18n.t("contractTypes:contractType.messages.removeSuccess", {
          defaultValue: "Contract Type removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Contract Type error:", error);
      // The API refuses deletion while contracts or staff still use the
      // record and says which — show that reason, not a generic failure.
      const fallback = i18n.t("contractTypes:contractType.messages.removeError", {
        defaultValue: "Failed to remove contract type",
      });
      reportDeleteFailure(error, fallback);
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default contractTypesSlice.reducer;

export const activateContractType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    // Optimistic, then rolled back on failure — same shape as the
    // department/business-unit toggles.
    dispatch(updateSingleContractType({ id, isActive: true } as any));

    try {
      await contractTypesApi.activateContractType(id);

      toast.success(
        i18n.t("common:slices.contractTypes.activateSuccess", {
          defaultValue: "Contract Type activated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleContractType({ id, isActive: false } as any));
      console.error("Activate Contract Type error:", error);
      toast.error(
        i18n.t("common:slices.contractTypes.activateError", {
          defaultValue: "Failed to activate contract type",
        }),
      );
    }
  };
};

export const deactivateContractType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(updateSingleContractType({ id, isActive: false } as any));

    try {
      await contractTypesApi.deactivateContractType(id);

      toast.success(
        i18n.t("common:slices.contractTypes.deactivateSuccess", {
          defaultValue: "Contract Type deactivated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleContractType({ id, isActive: true } as any));
      console.error("Deactivate Contract Type error:", error);
      toast.error(
        i18n.t("common:slices.contractTypes.deactivateError", {
          defaultValue: "Failed to deactivate contract type",
        }),
      );
    }
  };
};
